import { Box } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ReactNode, useRef, useContext } from "react";
import Draggable from "react-draggable";

import { LayoutContext } from "../../contexts/layout/context";
import {
  RIGHT_PANEL_MAX_WIDTH,
  RIGHT_PANEL_MIN_WIDTH,
  SIDE_NAV_WIDTH,
} from "../layout/const";

export const RightPanel = (props: { children: ReactNode }) => {
  const { children } = props;
  const {
    rightPanelWidth,
    setRightPanelWidth,
    isSidebarOpen,
    isNucleusExpanded = false,
  } = useContext(LayoutContext);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const compact = useMediaQuery("(max-width:900px)");
  const expandedWorkspace = isNucleusExpanded && !compact;

  // Handler for drag event
  const handleDrag = (e: any, data: any) => {
    if (!setRightPanelWidth) return;
    const newWidth = Math.max(
      RIGHT_PANEL_MIN_WIDTH,
      Math.min(RIGHT_PANEL_MAX_WIDTH, rightPanelWidth - data.deltaX),
    );
    setRightPanelWidth(newWidth);
  };

  return (
    <Box
      data-testid="nucleus-right-panel"
      ref={panelRef}
      sx={{
        width: isSidebarOpen
          ? compact
            ? "100vw"
            : expandedWorkspace
              ? `calc(100vw - ${SIDE_NAV_WIDTH}px)`
              : rightPanelWidth
          : 0,
        height: compact ? "100dvh" : "100%",
        borderLeft: isSidebarOpen ? "1px solid" : "none",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        pointerEvents: isSidebarOpen ? "auto" : "none",
        opacity: isSidebarOpen ? 1 : 0,
        right: 0,
        top: 0,
        zIndex: 1200,
        background: "#fff",
        // Desktop expansion participates in the app shell so the left-hand
        // navigation remains visible and interactive. Compact layouts retain
        // the existing modal treatment because there is not enough room for
        // two simultaneous workspaces.
        position: compact ? "fixed" : "relative",
        ...(compact && { inset: 0 }),
        flexShrink: 0,
        boxShadow: isSidebarOpen
          ? "-8px 0 24px rgba(15, 23, 42, 0.08)"
          : "none",
        transition:
          "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s, max-width 0.3s, opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Draggable handle only when open */}
      {isSidebarOpen && !compact && !expandedWorkspace && (
        <Draggable
          axis="x"
          onDrag={handleDrag}
          position={{ x: 0, y: 0 }}
          nodeRef={dragHandleRef}
        >
          <div
            ref={dragHandleRef}
            className="drag-handle"
            style={{
              width: 6,
              position: "absolute",
              left: -3,
              top: 0,
              bottom: 0,
              zIndex: 1000,
              background: "rgba(148, 163, 184, 0.2)",
            }}
          />
        </Draggable>
      )}
      {children}
    </Box>
  );
};
